import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { StatusBadge } from '@/components/status-badge'
import { cn } from '@/lib/utils'
import { SelectAllCheckbox, SelectCheckbox } from '@/src/components/BatchActions'
import type { RowSelection } from '@/src/hooks/useRowSelection'

/**
 * A list on a phone: one card per record instead of a table squeezed to its
 * first column. A card leads with what identifies the record (its code and
 * title), then who it is for and where it stands, then what happens next.
 * The whole card opens the record; its checkbox and quick edit are their own
 * tap targets above it. Under `md` only: a wider screen keeps the table.
 */
export function RecordCards<T extends { id: string }>({
  label,
  noun,
  rows,
  selection,
  children,
}: {
  /** Names the list for a screen reader, e.g. "Consignments". */
  label: string
  /** One record, for the select-all checkbox: "consignment". */
  noun: string
  rows: readonly T[]
  selection?: RowSelection<T>
  children: ReactNode
}) {
  return (
    <div className="md:hidden">
      {selection && rows.length > 0 && (
        // The header checkbox of the table, for a phone in selection mode.
        <div
          className={cn(
            'flex items-center gap-3 border-b px-4 py-2.5 text-sm text-muted-foreground',
            !selection.selecting && 'max-sm:hidden',
          )}
        >
          <SelectAllCheckbox selection={selection} visible={rows} noun={noun} />
          <span>Select all shown</span>
        </div>
      )}
      <ul aria-label={label} className="divide-y">
        {children}
      </ul>
    </div>
  )
}

export function RecordCard<T extends { id: string }>({
  row,
  rows,
  href,
  code,
  title,
  status,
  details,
  next,
  note,
  action,
  selection,
  selectLabel,
}: {
  row: T
  /** Every row shown, for picking a range. */
  rows: readonly T[]
  href: string
  /** The record's code, e.g. XTLCSEA202610180001; it heads the title. */
  code?: string | null
  title: string
  status: string | null | undefined
  /** The second line: who it is for, where, whose, and when it is due. */
  details: ReactNode
  /** The third line, muted: what happens next. */
  next?: string | null
  /** Small print under the rest, such as a tracking number. */
  note?: ReactNode
  /** Quick edit, beside the card. */
  action?: ReactNode
  selection?: RowSelection<T>
  selectLabel?: string
}) {
  return (
    <li
      className="relative flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
      {...selection?.rowProps(row, rows)}
    >
      {selection && (
        <div
          className={cn('relative z-10 self-start pt-0.5', !selection.selecting && 'max-sm:hidden')}
        >
          <SelectCheckbox
            checked={selection.isSelected(row.id)}
            label={selectLabel ?? `Select ${title}`}
            onToggle={(range) => selection.toggle(row, rows, range)}
          />
        </div>
      )}
      <div className="min-w-0 flex-1 space-y-1">
        {/* The code heads the title, with the status beside it, so the title has the full width. */}
        <div className="flex items-center justify-between gap-3">
          <span aria-hidden className="truncate text-xs text-muted-foreground">
            {code}
          </span>
          <StatusBadge status={status} />
        </div>
        <Link
          to={href}
          className={cn(
            'line-clamp-2 text-sm font-medium break-words outline-none',
            // The link covers the card, so a tap anywhere on it opens the record.
            "after:absolute after:inset-0 after:content-[''] focus-visible:after:ring-[3px] focus-visible:after:ring-ring/50 focus-visible:after:ring-inset",
          )}
        >
          {code && <span className="sr-only">{code} </span>}
          {title}
        </Link>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {details}
        </div>
        {next && <p className="truncate text-xs text-muted-foreground">{next}</p>}
        {note}
      </div>
      {action && <div className="relative z-10 shrink-0">{action}</div>}
    </li>
  )
}

/** One fact on a card's second line, with an icon when it says what kind of fact it is. */
export function RecordFact({
  icon: Icon,
  label,
  className,
  children,
}: {
  icon?: LucideIcon
  /** Read before the fact by a screen reader, when the icon is all that says what it is. */
  label?: string
  className?: string
  children: ReactNode
}) {
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1', className)}>
      {Icon && <Icon aria-hidden className="size-3.5 shrink-0" />}
      {label && <span className="sr-only">{label} </span>}
      <span className="truncate">{children}</span>
    </span>
  )
}
